import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import type { RootState } from "../app/store";
import { logout, refreshToken } from "../app/slices/authSlice";
import { logger } from "../lib/logger";

// Custom base query with auth and error handling
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || "/api",
  prepareHeaders: (headers, { getState }) => {
    const authState = (getState() as RootState).auth;
    if (authState.token) {
      headers.set("authorization", `Bearer ${authState.token}`);
    }
    headers.set("X-Request-ID", crypto.randomUUID());
    // Inject Admin Mode header when super_admin has activated cross-tenant mode (RF-7)
    if (authState.adminMode) {
      headers.set("X-OXBO-Admin-Mode", "true");
    }
    return headers;
  },
});

// Single-flight refresh guard (FRD-OXBO-002 §RF-7).
// The refresh token ROTATES on use: the first refresh invalidates the old
// token server-side. Without this guard, N concurrent requests that all 401 on
// the same expired access token would each dispatch a refresh; only the first
// succeeds and the rest fail against the already-rotated token, spuriously
// logging the user out. We dedupe to one in-flight refresh and have all callers
// await it. The promise resolves to true on success, false on failure.
let refreshInFlight: Promise<boolean> | null = null;

// Base query with re-auth logic
const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Try to refresh the access token via oxbo-auth (FRD-OXBO-002 §RF-7).
    // Delegate to the refreshToken thunk so the rotating-refresh logic and
    // storage updates live in exactly one place (authSlice). The thunk targets
    // the oxbo-auth /v1/auth/refresh endpoint — NOT the biotk-api.
    const currentRefreshToken = (api.getState() as RootState).auth.refreshToken;

    if (currentRefreshToken) {
      // Coalesce concurrent refreshes into one rotation (see guard comment).
      if (!refreshInFlight) {
        refreshInFlight = api
          .dispatch(refreshToken())
          .then((res) => refreshToken.fulfilled.match(res))
          .finally(() => {
            refreshInFlight = null;
          });
      }
      const refreshed = await refreshInFlight;

      if (refreshed) {
        // Token rotated + stored by the thunk; retry the original request.
        result = await baseQuery(args, api, extraOptions);
      } else {
        api.dispatch(logout());
        logger.warn("Session expired, user logged out");
      }
    } else {
      api.dispatch(logout());
    }
  }

  if (result.error) {
    const url = typeof args === "string" ? args : args.url;
    logger.error("API request failed", {
      url,
      status: result.error.status,
      data: result.error.data,
    });
  }

  return result;
};

// Create the API slice
export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Borefield",
    "Bore",
    "Job",
    "Report",
    "Rig",
    "Survey",
    "ThermalTest",
    "Circuit",
    "User",
    "TestEquipment",
    "CalEvent",
    // PR2-B tag types
    "TimeReport",
    "LaborLog",
    "AdminUser",
    "BorefieldRole",
    "AuditLog",
    "Personnel",
    "Membership",
    // PR2-C tag types
    "ApiKey",
    "OrgSettings",
  ],
  endpoints: () => ({}),
});

// Export hooks for usage in components
export const {
  middleware: apiMiddleware,
  reducer: apiReducer,
  reducerPath,
} = apiSlice;
